import { Font } from "opentype.js";
import {
  Box3,
  BufferGeometry,
  Color,
  Group,
  Mesh,
  MeshBasicMaterial,
  PerspectiveCamera,
  ShapeBufferGeometry,
  Shape,
  Vector3,
  CircleBufferGeometry,
  Material,
  Object3D,
} from "three";
import { mergeBufferGeometries, SVGLoader } from "three-stdlib";
import {
  boxDims,
  cameraAzimuthElevation,
  disposeObjects,
} from "../../../lib/misc/util";
import { SceneObjectBase } from "../object";
import { positionAbbr } from "./actor";

const loader = new SVGLoader();

/**
 * Implements a nameplate class for players including last name and current position
 * The nameplate pivots around the player and always points to the camera
 * Spec: https://www.figma.com/file/0J3aKjKhdwFSlT61JLpIhy/Gameday-3D?node-id=1%3A2681
 */
export class Nameplate extends SceneObjectBase {
  private group: Group = new Group();

  private leftNameplate: Group = new Group();
  private rightNameplate: Group = new Group();

  private name: string;
  private pos: string;
  private batSide: string;
  private font: Font;
  private altFont?: Font;

  private box = new Box3();
  private nameplateOffset = new Vector3(2, 2, 0);
  private position = new Vector3();

  private nameHeightOffset = 0.1;
  private nameWidth: number = 0;
  private halfTotalWidth: number = 0;
  private targetHeight = 0.75;

  private scaleFactor = 0.01;
  private scaleLimits = [1, 5];

  private primaryMat: MeshBasicMaterial;
  private whiteMat: MeshBasicMaterial;

  constructor(
    name: string,
    posId: number,
    batSide: string,
    opacity: number,
    font: Font,
    altFont?: Font,
    color?: Color
  ) {
    super();

    this.name = name;
    this.pos = positionAbbr.get(posId) ?? "?";
    this.font = font;
    this.altFont = altFont;
    this.batSide = batSide;

    this.primaryMat = new MeshBasicMaterial({
      color: color,
      transparent: true,
    });
    this.whiteMat = new MeshBasicMaterial({
      color: "white",
      transparent: true,
    });

    this.makeNameplates();

    this.leftNameplate.name = "nameplate-left";
    this.rightNameplate.name = "nameplate-right";

    this.group.add(this.leftNameplate);
    this.group.add(this.rightNameplate);

    this.setOpacity(opacity);
  }

  get object(): Object3D {
    return this.group;
  }

  /**
   * Set position of nameplates (left and right)
   * @param pos position to set to
   */
  update(pos: Vector3, opacity: number) {
    this.group.position.copy(pos);

    this.setOpacity(opacity);
  }

  teardown() {
    this.object.traverse(disposeObjects);
    //@ts-ignore
    this.font = undefined;
    this.altFont = undefined;
  }

  private setOpacity(opacity: number) {
    this.group.traverse((o) => {
      if (o instanceof Mesh && o.material instanceof Material) {
        o.material.opacity = opacity;
      }
    });
  }

  /**
   * Update rotation and scale of plates - called in render loop as depends on
   * camera position
   * Toggles right or left side plate visibility for batter depending on camera angle
   * to prevent the nameplate covering home plate
   * @param camera
   */
  updateTransform(camera: PerspectiveCamera) {
    if (!this.group.visible) {
      return;
    }

    this.group.getWorldPosition(this.position);
    const dist = this.position.distanceTo(camera.position);

    let rightHanded = this.batSide == "R";
    let azEl = cameraAzimuthElevation(camera);
    if (this.pos == "B" && Math.abs(azEl[0]) < Math.PI / 2) {
      this.leftNameplate.visible = rightHanded;
      this.rightNameplate.visible = !rightHanded;
    } else {
      this.leftNameplate.visible = !rightHanded;
      this.rightNameplate.visible = rightHanded;
    }

    this.group.rotation.y = azEl[0];
    this.leftNameplate.rotation.x = -azEl[1];
    this.rightNameplate.rotation.x = -azEl[1];

    const scale = Math.min(
      this.scaleLimits[1],
      Math.max(this.scaleFactor * dist, this.scaleLimits[0])
    );
    this.leftNameplate.scale.set(scale, scale, scale);
    this.rightNameplate.scale.set(scale, scale, scale);

    this.leftNameplate.position.x =
      -(scale * this.halfTotalWidth) - this.nameplateOffset.x;
    this.rightNameplate.position.x =
      scale * this.halfTotalWidth + this.nameplateOffset.x;
    this.group.position.y = Math.max(
      this.nameplateOffset.y,
      this.targetHeight * scale
    );
  }

  /**
   * Create both left and right nameplates
   * Elements are ordered in the z-axis, plate(0.0), badge(0.025), text(0.05)
   * TODO: use instancedMesh? might not be much benefit between just two copies
   */
  private makeNameplates() {
    const nameLeft = this.makeText(this.name, this.primaryMat, this.font, 1.0);
    this.nameWidth = boxDims(this.box.setFromObject(nameLeft)).x;
    const posLeft = this.makeText(this.pos, this.whiteMat, this.altFont, 0.6);
    const badgeLeft = this.makeBadge();
    const plateLeft = this.makePlateShape();

    // bodge to vertically centralise name
    nameLeft.position.y += this.nameHeightOffset;

    const nameRight = nameLeft.clone();
    const posRight = posLeft.clone();
    const badgeRight = badgeLeft.clone();
    const plateRight = plateLeft.clone();

    nameLeft.position.x += -this.targetHeight;
    posLeft.position.x += (this.nameWidth + this.targetHeight) / 2;
    badgeLeft.position.x += (this.nameWidth + this.targetHeight) / 2;

    nameLeft.name = "nameplate-name";
    posLeft.name = "nameplate-pos";
    badgeLeft.name = "nameplate-badge";

    this.leftNameplate.add(nameLeft);
    this.leftNameplate.add(posLeft);
    this.leftNameplate.add(badgeLeft);
    this.leftNameplate.add(plateLeft);

    nameRight.position.x += this.targetHeight * 0.875;
    posRight.position.x += -(this.nameWidth + this.targetHeight * 1.125) / 2;
    badgeRight.position.x += -(this.nameWidth + this.targetHeight) / 2;

    this.rightNameplate.add(nameRight);
    this.rightNameplate.add(posRight);
    this.rightNameplate.add(badgeRight);
    this.rightNameplate.add(plateRight);

    this.halfTotalWidth = (this.nameWidth + 3 * this.targetHeight) / 2;
    this.leftNameplate.position.x = -(
      this.nameplateOffset.x + this.halfTotalWidth
    );
    this.rightNameplate.position.x =
      this.nameplateOffset.x + this.halfTotalWidth;
  }

  /**
   * Converts a string into a Mesh object
   * @param relativeScale scale relative to targetHeight
   * @returns the Mesh of the string
   */
  private makeText(
    text: string = "?",
    material: MeshBasicMaterial = this.primaryMat,
    font: Font = this.font,
    relativeScale: number = 1
  ): Mesh {
    const geometries: BufferGeometry[] = [];

    const path = font.getPath(text, 0, 0, 60);
    const svg = path.toSVG(2);

    const data = loader.parse(svg);
    const paths = data.paths;

    for (let i = 0; i < paths.length; i++) {
      const path = paths[i];

      const shapes = SVGLoader.createShapes(path);

      for (let j = 0; j < shapes.length; j++) {
        const shape = shapes[j];
        const geometry = new ShapeBufferGeometry(shape);
        geometries.push(geometry);
      }
    }

    const merged = mergeBufferGeometries(geometries);
    if (merged) {
      let mesh = new Mesh(merged, material);

      this.box.setFromObject(mesh);
      const { x, y } = boxDims(this.box);
      const scale = (relativeScale * this.targetHeight) / y;

      mesh.scale.set(scale, -scale, scale); // -scale for y corrects text flip
      mesh.position.set((-x * scale) / 2, (-y * scale) / 2, 0.05); // center the text on (0, 0)

      return mesh;
    } else {
      console.error("issue merging nameplate geometry");
    }

    return new Mesh();
  }

  /**
   * Creates the rounded rectangle plate shape based on the targetHeight and
   * width of the players name text
   * @returns the plate Mesh
   */
  private makePlateShape(): Mesh {
    const plateShape = new Shape();

    let h = this.targetHeight;
    let radius = this.targetHeight;
    let halfW = (this.nameWidth + this.targetHeight) / 2;
    plateShape.moveTo(-halfW, h);
    plateShape.lineTo(halfW, h);
    plateShape.absarc(halfW, 0, radius, 0.5 * Math.PI, -0.5 * Math.PI, true);
    plateShape.lineTo(-halfW, -h);
    plateShape.absarc(-halfW, 0, radius, -0.5 * Math.PI, 0.5 * Math.PI, true);

    const geometry = new ShapeBufferGeometry(plateShape);
    const mesh = new Mesh(geometry, this.whiteMat);
    mesh.position.z = 0.0;

    return mesh;
  }

  /**
   * Makes the circular badge behind the player's position
   * @returns the badge Mesh
   */
  private makeBadge(): Mesh {
    const geometry = new CircleBufferGeometry(0.875 * this.targetHeight, 32);
    const mesh = new Mesh(geometry, this.primaryMat);
    mesh.position.z = 0.025;

    return mesh;
  }
}
